import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OrderService } from '../../appServices/order.service';
import { TenantResolver } from '../../appServices/tenant/tenant.resolver';
import { AutomaticPrintOutboxService } from '../printing/automatic-print-outbox.service';
import { StorageService } from '../services/storage.service';
import { createSecurePaymentUuid } from './secure-payment-reference';
import { PaymentSaleGuardService } from './payment-sale-guard.service';
import { MyPosSplitPaymentIdentityService } from './mypos-split-payment-identity.service';

const CASH_JOURNAL_KEY = 'tapgo_pos_cash_journal_v1';

type CashOperationType = 'guest' | 'parent' | 'mark-paid' | 'partial';

interface CashJournalEntry {
  version: 1;
  operationReference: string;
  tenant: string;
  saleKey: string;
  type: CashOperationType;
  orderId?: number;
  payload: any;
  printRoles: Array<'KOT' | 'BILLING'>;
  createdAt: number;
}

interface CashCompletedEntry {
  version: 1;
  operationReference: string;
  tenant: string;
  saleKey: string;
  type: CashOperationType;
  orderId?: number;
  response: any;
  completedAt: number;
}

interface CashJournalState {
  version: 2;
  pending: CashJournalEntry[];
  completed: CashCompletedEntry[];
}

@Injectable({ providedIn: 'root' })
export class CashPaymentJournalService {
  private loaded = false;
  private entries: CashJournalEntry[] = [];
  private completed: CashCompletedEntry[] = [];
  private inFlight = new Map<string, Promise<any>>();
  private localSaleKeys = new WeakMap<object, string>();

  constructor(
    private storage: StorageService,
    private tenantResolver: TenantResolver,
    private orderService: OrderService,
    private printOutbox: AutomaticPrintOutboxService,
    private saleGuard: PaymentSaleGuardService,
    private splitPaymentIdentity: MyPosSplitPaymentIdentityService,
  ) {}

  submitCheckout(
    payload: any,
    parent = false,
    saleReference?: string,
  ): Promise<any> {
    return this.submit(
      parent ? 'parent' : 'guest',
      payload,
      undefined,
      parent ? ['BILLING'] : ['KOT', 'BILLING'],
      saleReference,
    );
  }

  submitMarkPaid(
    orderId: number,
    payload: any,
    printRoles: Array<'KOT' | 'BILLING'> = ['BILLING'],
    saleReference = `order:${orderId}:settlement`,
  ): Promise<any> {
    return this.submit(
      'mark-paid',
      payload,
      orderId,
      printRoles,
      saleReference,
    );
  }

  submitPartial(
    orderId: number,
    payload: any,
    saleReference = `order:${orderId}:partial:${Number(
      payload?.amount ?? payload?.totalAmount,
    ).toFixed(2)}`,
  ): Promise<any> {
    return this.submit('partial', payload, orderId, [], saleReference);
  }

  async resumePending(): Promise<void> {
    await this.load();
    const tenant = (await this.tenantResolver.getTenantName())?.trim();
    if (!tenant) {
      return;
    }
    for (const entry of this.entries.filter((item) => item.tenant === tenant)) {
      try {
        await this.saleGuard.reserve(
          entry.tenant,
          entry.saleKey,
          'cash',
        );
        await this.execute(entry);
      } catch (error) {
        console.error(
          'Pending cash operation remains queued',
          entry.operationReference,
          error,
        );
      }
    }
  }

  private async submit(
    type: CashOperationType,
    sourcePayload: any,
    orderId?: number,
    printRoles: Array<'KOT' | 'BILLING'> = [],
    explicitSaleReference?: string,
  ): Promise<any> {
    await this.load();
    const tenant = (await this.tenantResolver.getTenantName())?.trim();
    if (!tenant) {
      throw new Error('Tenant is required before recording a cash payment.');
    }

    const objectKey =
      sourcePayload && typeof sourcePayload === 'object'
        ? this.localSaleKeys.get(sourcePayload)
        : undefined;
    const matchingPending = this.entries.find(
      (item) =>
        item.tenant === tenant &&
        item.type === type &&
        (explicitSaleReference
          ? item.saleKey === explicitSaleReference
          : objectKey
            ? item.saleKey === objectKey
            : !!orderId && item.orderId === orderId),
    );
    const saleKey =
      `${explicitSaleReference || objectKey || matchingPending?.saleKey || ''}`.trim() ||
      createSecurePaymentUuid();
    // Reserve before consulting a cached completed response: an opposite
    // myPOS operation for this canonical identity must always win visibly,
    // including after a restart or a lost backend ACK.
    await this.saleGuard.reserve(tenant, saleKey, 'cash');
    const completed = this.completed.find(
      (item) =>
        item.tenant === tenant &&
        item.saleKey === saleKey &&
        item.type === type,
    );
    if (completed) {
      await this.finishRecordedGuard(completed);
      return this.clone(completed.response);
    }
    if (sourcePayload && typeof sourcePayload === 'object') {
      this.localSaleKeys.set(sourcePayload, saleKey);
    }
    const operationReference =
      matchingPending?.operationReference ||
      this.operationReferenceForSale(saleKey);

    let entry = this.entries.find(
      (item) =>
        item.tenant === tenant &&
        item.saleKey === saleKey &&
        item.type === type,
    );
    if (!entry) {
      const payload = JSON.parse(
        JSON.stringify({
          ...sourcePayload,
          operationReference,
          ...(
            type === 'guest' || type === 'parent'
              ? { uuid: operationReference }
              : {}
          ),
        }),
      );
      entry = {
        version: 1,
        operationReference,
        tenant,
        saleKey,
        type,
        orderId,
        payload,
        printRoles,
        createdAt: Date.now(),
      };
      this.entries.push(entry);
      try {
        await this.persist();
      } catch (error) {
        this.entries = this.entries.filter(
          (item) => item.operationReference !== operationReference,
        );
        await this.saleGuard.releaseNotStarted(
          tenant,
          saleKey,
          'cash',
        );
        throw error;
      }
    } else {
    }
    return this.execute(entry);
  }

  private execute(entry: CashJournalEntry): Promise<any> {
    const running = this.inFlight.get(entry.operationReference);
    if (running) {
      return running;
    }

    const request = this.executeOnce(entry).finally(() => {
      this.inFlight.delete(entry.operationReference);
    });
    this.inFlight.set(entry.operationReference, request);
    return request;
  }

  private async executeOnce(entry: CashJournalEntry): Promise<any> {
    // From this point the backend may accept the payment even if its response
    // is lost. Keep the durable lock unresolved until a completed tombstone
    // has been committed.
    await this.saleGuard.mark(
      entry.tenant,
      entry.saleKey,
      'cash',
      'uncertain',
      entry.operationReference,
    );
    let response: any;
    switch (entry.type) {
      case 'parent':
        response = await firstValueFrom(
          this.orderService.parentCheckout(entry.payload),
        );
        break;
      case 'mark-paid':
        response = await firstValueFrom(
          this.orderService.markCashOrderAsPaid(entry.orderId, entry.payload),
        );
        break;
      case 'partial':
        response = await firstValueFrom(
          this.orderService.createCashPaymentByOrderId(
            entry.orderId,
            entry.payload,
          ),
        );
        break;
      case 'guest':
      default:
        response = await firstValueFrom(
          this.orderService.guestCheckout(entry.payload),
        );
        break;
    }

    const confirmedOrderId =
      entry.type === 'mark-paid'
        ? entry.orderId
        : Number(response?.id);
    if (
      entry.type !== 'partial' &&
      Number.isInteger(confirmedOrderId) &&
      confirmedOrderId > 0
    ) {
      for (const role of entry.printRoles || []) {
        if (role === 'KOT') {
          await this.printOutbox.planKOT(confirmedOrderId);
        } else {
          await this.printOutbox.planBilling(confirmedOrderId);
        }
      }
    }

    const completed: CashCompletedEntry = {
      version: 1,
      operationReference: entry.operationReference,
      tenant: entry.tenant,
      saleKey: entry.saleKey,
      type: entry.type,
      orderId: entry.orderId,
      // A checkout response can contain the complete order (items/customer).
      // The crash tombstone only needs the stable order id; keeping full
      // responses would eventually exceed the native durable-store limit.
      response: this.completedResponse(entry, response),
      completedAt: Date.now(),
    };
    this.completed = [
      completed,
      ...this.completed.filter(
        (item) =>
          !(
            item.tenant === completed.tenant &&
            item.saleKey === completed.saleKey &&
            item.type === completed.type
          ),
      ),
    ].slice(0, 100);
    this.entries = this.entries.filter(
      (item) => item.operationReference !== entry.operationReference,
    );
    await this.persist();
    await this.finishRecordedGuard(completed);
    return response;
  }

  private async finishRecordedGuard(
    completed: CashCompletedEntry,
  ): Promise<void> {
    await this.saleGuard.mark(
      completed.tenant,
      completed.saleKey,
      'cash',
      'recorded',
      completed.operationReference,
    );
    if (
      completed.type === 'partial' &&
      completed.orderId &&
      completed.saleKey.startsWith('split-')
    ) {
      // Required cleanup order for a reusable equal split: backend ACK and
      // completed tombstone -> recorded guard -> rotate business identity ->
      // only then remove the active lock.
      await this.splitPaymentIdentity.complete(
        completed.orderId,
        completed.saleKey,
      );
    }
    await this.saleGuard.finalizeRecorded(
      completed.tenant,
      completed.saleKey,
      'cash',
    );
  }

  private async load(): Promise<void> {
    if (this.loaded) {
      return;
    }
    const raw = await this.storage.getStrict(CASH_JOURNAL_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.entries = parsed;
      } else if (
        parsed?.version === 2 &&
        Array.isArray(parsed.pending) &&
        Array.isArray(parsed.completed)
      ) {
        this.entries = parsed.pending;
        this.completed = parsed.completed;
      } else {
        throw new Error('Cash payment journal is invalid.');
      }
    }
    this.loaded = true;
  }

  private async persist(): Promise<void> {
    const state: CashJournalState = {
      version: 2,
      pending: this.entries,
      completed: this.completed,
    };
    await this.storage.set(CASH_JOURNAL_KEY, JSON.stringify(state));
  }

  private operationReferenceForSale(saleKey: string): string {
    if (
      saleKey.length === 0 ||
      saleKey.length > 128 ||
      !/^[\x20-\x7E]+$/.test(saleKey)
    ) {
      throw new Error('Cash sale reference must be printable ASCII (max 128 characters).');
    }
    return saleKey;
  }

  private completedResponse(entry: CashJournalEntry, response: any): any {
    const responseId = Number(response?.id);
    if (Number.isInteger(responseId) && responseId > 0) {
      return { id: responseId };
    }
    if (
      Number.isInteger(entry.orderId) &&
      Number(entry.orderId) > 0
    ) {
      return { id: Number(entry.orderId), ok: true };
    }
    return { ok: true };
  }

  private clone<T>(value: T): T | null {
    if (value === undefined) {
      return null;
    }
    return JSON.parse(JSON.stringify(value));
  }
}
